import { useEffect } from "react";
import type { LiveCall } from "../lib/live-call";
import { startLiveCall } from "../lib/live-call";

type BridgeWindow = Window & { ReactNativeWebView?: { postMessage(value: string): void } };

/** Native keeps its call UI; this same-origin surface owns only WebRTC media. */
export function CallAudioPage() {
  useEffect(() => {
    if (!(window as BridgeWindow).ReactNativeWebView) return;
    let muted = false;
    let call: LiveCall | null = null;
    let controller: AbortController | undefined;
    const post = (message: unknown) =>
      (window as BridgeWindow).ReactNativeWebView?.postMessage(JSON.stringify(message));
    const receive = (event: Event) => {
      if (
        (event as MessageEvent).origin &&
        (event as MessageEvent).origin !== window.location.origin
      )
        return;
      let message: {
        type: string;
        botId?: string;
        callId?: string;
        headers?: Record<string, string>;
        muted?: boolean;
      };
      try {
        message = JSON.parse((event as MessageEvent).data);
      } catch {
        return;
      }
      if (message.type === "start" && !controller && message.botId && message.callId) {
        muted = message.muted ?? false;
        controller = new AbortController();
        const signal = controller.signal;
        void startLiveCall({
          botId: message.botId,
          callId: message.callId,
          headers: message.headers,
          signal,
          muted: () => muted,
          onMedia: (control) => {
            call = control;
          },
          onState: (state) => post({ type: "state", state }),
          onClose: () => post({ type: "closed" }),
          onError: () => post({ type: "error" }),
        })
          .then((connection) => {
            if (signal.aborted) {
              connection?.close();
              return;
            }
            call = connection;
            call?.mute(muted);
            post({ type: connection ? "connected" : "unsupported" });
          })
          .catch(() => {
            if (!signal.aborted) post({ type: "error" });
          });
      } else if (message.type === "mute") {
        muted = message.muted ?? false;
        call?.mute(muted);
      } else if (message.type === "close") {
        controller?.abort();
        call?.close();
      }
    };
    window.addEventListener("message", receive);
    document.addEventListener("message", receive);
    post({ type: "ready" });
    return () => {
      controller?.abort();
      call?.close();
      window.removeEventListener("message", receive);
      document.removeEventListener("message", receive);
    };
  }, []);
  return null;
}
